import { z } from 'zod'
import { EventType } from './schemas/events'

// SubjectType mirrors the conceptual tag on events.subject_id. It's not a DB
// enum (events.subject_type is text) because the set will grow over time
// without schema churn; zod guards the application-layer write path instead.
export const SubjectType = z.enum(['document', 'client', 'user', 'pipeline'])
export type SubjectType = z.infer<typeof SubjectType>

// EventInsert shape — matches public.events columns 1:1 (snake_case).
// The actual DB insert is the caller's job so this module stays zod-only
// (packages/shared is importable from Deno Edge Functions — no Supabase SDK).
export const EventInsert = z.object({
  type: EventType,
  actor_id: z.string().uuid().nullable(),
  subject_type: SubjectType,
  subject_id: z.string().min(1),
  // Which firm the event belongs to. Optional so single-firm callers can still let the column
  // DEFAULT apply, but the schema has to CARRY it — zod strips unknown keys, so before this a
  // caller that passed firm_id would have had it silently dropped on the way to the insert.
  firm_id: z.string().uuid().nullable().optional(),
  payload: z.record(z.unknown()).default({}),
})
export type EventInsert = z.infer<typeof EventInsert>

type BuildEventParams = {
  type: z.infer<typeof EventType>
  actorId: string | null
  subjectType: SubjectType
  subjectId: string
  firmId?: string | null
  payload?: Record<string, unknown>
}

// Validate + shape an event row for `insert into public.events`. Throws
// ZodError if the payload violates the schema — the caller should treat this
// as a programmer error (the audit trail should never silently swallow a bad
// event).
export function buildEvent(params: BuildEventParams): EventInsert {
  return EventInsert.parse({
    type: params.type,
    actor_id: params.actorId,
    subject_type: params.subjectType,
    subject_id: params.subjectId,
    ...(params.firmId !== undefined ? { firm_id: params.firmId } : {}),
    payload: params.payload ?? {},
  })
}

// Per plan §4.5: client_config.edited events carry { field, from, to } so the
// audit reader can reconstruct what changed without diffing the whole row.
export function configDiffPayload<T>(
  field: string,
  from: T,
  to: T,
): Record<string, unknown> {
  return { field, from, to }
}

type FieldChange<T> = { field: string; from: T; to: T }

// Shallow diff between two records — returns one FieldChange per changed key.
// Arrays are compared element-wise (order-sensitive). Objects/JSONB payloads
// aren't part of the admin config surface, so we don't recurse — if a caller
// passes an object value, it's compared by reference, which will always emit
// a change. That's a caller problem: don't feed nested objects to this helper.
export function shallowDiff<T extends Record<string, unknown>>(
  before: T,
  after: T,
): FieldChange<unknown>[] {
  const changes: FieldChange<unknown>[] = []
  const keys = new Set([...Object.keys(before), ...Object.keys(after)])
  for (const key of keys) {
    const fromVal = before[key]
    const toVal = after[key]
    if (!isEqual(fromVal, toVal)) {
      changes.push({ field: key, from: fromVal, to: toVal })
    }
  }
  return changes
}

function isEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((v, i) => isEqual(v, b[i]))
  }
  return false
}
